import React, { useCallback, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Platform, StyleSheet } from 'react-native';
import { logger as LOG } from '@garnish/logger';
import {
  HostedFrame,
  type HostedFrameMessageEvent,
  type HostedFrameRef,
} from '@sg/garnish';

import { useIsLoggedIn } from '@order/AuthMachine';
import { useCustomer } from '@order/Customer';
import { getEnvVars } from '@order/utils';

import {
  SierraContainer,
  SierraErrorMessage,
  SierraHeader,
  SierraLoadingIndicator,
} from './components';
import { useChatTokenQuery } from './GraphQL/ChatToken.generated';

export const SierraHostedFrame = (props: HostedFrameProps) => {
  const { onClose } = props;
  const { formatMessage } = useIntl();

  // ─── State ───────────────────────────────────────────────────────────

  const isLoggedIn = useIsLoggedIn();
  const [isOpen, setIsOpen] = useState(false);
  const frameRef = useRef<HostedFrameRef>(null);

  // ─── Remote Data ─────────────────────────────────────────────────────

  const { customer } = useCustomer();
  const [tokenResponse] = useChatTokenQuery({ pause: !isLoggedIn });

  const customerId = customer.id;
  const authToken = tokenResponse.data?.chatToken?.token;
  const isFetchingToken = tokenResponse.fetching;
  const hasError = tokenResponse.error;

  // ─── Messaging ───────────────────────────────────────────────────────

  const handleOnMessage = useCallback(
    async (message: HostedFrameMessageEvent) => {
      const data = parseMessage(message);
      const { log, error, ready, loaded, opened, closed } = data;

      if (log) {
        logger.info(log);
      }

      if (error) {
        logger.error(error);
      }

      if (ready) {
        frameRef.current?.postMessage?.(INIT_ACTION(customerId, authToken));
      }

      if (loaded) {
        frameRef.current?.postMessage?.(OPEN_ACTION);
      }

      if (opened) {
        setIsOpen(true);
      }

      if (closed) {
        onClose();
      }
    },
    [customerId, authToken, onClose],
  );

  // ─────────────────────────────────────────────────────────────────────

  return (
    <SierraContainer>
      {isOpen && !hasError ? null : <SierraHeader onClose={onClose} />}

      {hasError ? <SierraErrorMessage /> : null}

      {isOpen || hasError ? null : <SierraLoadingIndicator />}

      {isFetchingToken || hasError ? null : (
        <HostedFrame
          title={formatMessage(messages.title)}
          ref={frameRef}
          frameId={FRAME_ID}
          frameHeight="100%"
          style={styles.frame}
          containerStyle={isOpen ? styles.frame : styles.hideContent}
          source={SOURCE}
          sharedCookiesEnabled={true}
          thirdPartyCookiesEnabled={true}
          onMessage={handleOnMessage}
        />
      )}
    </SierraContainer>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

LOG.disable('Sierra');
const logger = LOG.extend('Sierra');
const FRAME_ID = 'sierra';
const URL = getEnvVars().SIERRA_EMBEDDED_CHATBOT_URL;
const SOURCE = { uri: URL };

// ─── Actions ─────────────────────────────────────────────────────────────────

const OPEN_ACTION = JSON.stringify({ type: 'OPEN' });
const INIT_ACTION = (customerId?: string, authToken?: string) =>
  JSON.stringify({
    type: 'INITIALIZE',
    customerId,
    authToken,
    platform: Platform.OS,
  });

// ─── Helpers ─────────────────────────────────────────────────────────────────

const parseMessage = (event: HostedFrameMessageEvent) => {
  try {
    const dataString = event.nativeEvent.data ?? '{}';
    const parsedData = JSON.parse(dataString) as HostedFieldsPayload;

    return parsedData;
  } catch {
    return { error: 'Failed to parse JSON message.' };
  }
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  title: {
    defaultMessage: 'Sierra Chat',
    description: 'Sierra | Chat | Title',
  },
  close: {
    defaultMessage: 'Close Chat',
    description: 'Sierra | Chat | Close',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  frame: {
    flex: 1,
  },
  hideContent: {
    opacity: 0,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type HostedFieldsPayload = {
  log?: string;
  error?: string;
  ready?: boolean;
  loaded?: boolean;
  opened?: boolean;
  closed?: boolean;
  initialized?: boolean;
};

type HostedFrameProps = {
  onClose: () => void;
};
